import { Box, styled } from '@mui/material';
import SelectMenu from '../../../forms/SelectMenu';
import { useTranslation } from 'react-i18next';
import {
  ActionType,
  SimplifiedWorkflowActionStepFormData,
  StepType,
} from '../../../../data/models/simplified-workflow.model';
import { useFormContext, useWatch } from 'react-hook-form';
import lowerFirst from 'lodash/lowerFirst';
import SendMessageActionForm from './SendMessageActionForm';
import { useEffect } from 'react';

const PREFIX = 'ActionStepForm';

const classes = {
  actionSelect: `${PREFIX}-actionSelect`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.actionSelect}`]: {
    marginBottom: theme.spacing(3),
  },
}));

interface ActionStepFormProps {
  handleFormUpdate: () => void;
}

export default function ActionStepForm({
  handleFormUpdate,
}: ActionStepFormProps) {
  const { t } = useTranslation('common');
  const { control, formState, setValue, getValues, reset } =
    useFormContext<SimplifiedWorkflowActionStepFormData>();

  const selectableActionTypes = Object.values(ActionType).map((value) => ({
    key: t(`workflows.${lowerFirst(value)}Action`),
    value,
  }));

  const actionType = useWatch({ control, name: 'actionType' });
  const getTitle = (actionType: ActionType) => {
    const actionTypeTranslated = t(`workflows.${lowerFirst(actionType)}Action`);
    const title = t('workflows.actionWithType', {
      actionType: actionTypeTranslated,
    });
    return title;
  };

  useEffect(() => {
    if (actionType) {
      const title = getTitle(actionType);
      setValue('title', title);
    }

    handleFormUpdate();
  }, [actionType]);

  const handleActionTypeChange = (event: any) => {
    const actionType = event.target.value;
    if (actionType) {
      const currentValues = getValues();
      const title = getTitle(actionType);
      setValue('title', title);
      reset({
        actionType,
        type: StepType.Action,
        title,
        tempId: currentValues.tempId,
        parentId: currentValues.parentId,
        branch: currentValues.branch,
      });
      if (actionType === ActionType.SendMessage && currentValues.branch) {
        setValue('supportsInteractive' as any, false);
      }
      handleFormUpdate();
    }
  };

  return (
    <StyledBox>
      <SelectMenu
        name="actionType"
        label={t('workflows.action')}
        className={classes.actionSelect}
        values={selectableActionTypes}
        valueAttr="value"
        labelAttr="key"
        control={control}
        required
        errorMessage={formState.errors?.actionType?.message}
        rules={{ required: t('common.requiredField') }}
        onChange={handleActionTypeChange}
      />
      {actionType === ActionType.SendMessage && (
        <SendMessageActionForm handleFormUpdate={handleFormUpdate} />
      )}
    </StyledBox>
  );
}
